import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button } from '@/shared/ui';

import { useResolveRequest } from './board-api';
import { UserSelect } from './UserSelect';
import { useWorkspaceMembers } from './workspace-api';
import { ROLE_KEYS } from './roles';
import type { AccessRequest, MemberRole } from './types';
import styles from './Collab.module.css';

/** Approval queue: join requests (moderated links) and incoming board transfers. */
export function RequestsList({
  requests,
  canResolve,
}: {
  requests: AccessRequest[];
  canResolve: boolean;
}) {
  const { t, i18n } = useTranslation();
  const resolve = useResolveRequest();
  const [nominees, setNominees] = useState<Record<string, string | null>>({});
  if (requests.length === 0) return <p className={styles.muted}>{t('collab.requests.none')}</p>;
  return (
    <>
      <FormError error={resolve.error} />
      <ul className={styles.list}>
        {requests.map((request) => {
          const board =
            request.board_name ??
            (typeof request.payload['board_name'] === 'string'
              ? request.payload['board_name']
              : null);
          const role = request.payload['role'];
          const pending = request.status === 'pending';
          return (
            <li key={request.id} className={styles.row} data-testid="request-row">
              <span className={styles.rowMain}>
                <span>
                  {t(`collab.requests.kind_${request.kind}`, {
                    who: request.requester.display_name,
                    board: board ?? '',
                  })}
                  {typeof role === 'string' &&
                    role in ROLE_KEYS &&
                    ` · ${t(ROLE_KEYS[role as MemberRole])}`}
                  {typeof request.payload['role_name'] === 'string' &&
                    ` · ${request.payload['role_name']}`}
                </span>
                <span className={styles.rowMeta}>
                  {formatDateTime(request.created_at, i18n.language)} ·{' '}
                  {t(`collab.requests.status_${request.status}`)}
                  {request.resolved_by &&
                    ` · ${request.resolved_by.display_name}, ${formatDateTime(
                      request.resolved_at ?? request.created_at,
                      i18n.language,
                    )}`}
                </span>
              </span>
              {request.kind === 'board_transfer' && (
                <span className={styles.transferSides}>
                  <span data-approved={request.source_approved ? 'true' : 'false'}>
                    {t('collab.requests.sourceSide')}:{' '}
                    {request.source_approved
                      ? t('collab.requests.sideApproved')
                      : t('collab.requests.sidePending')}
                  </span>
                  <span data-approved={request.destination_approved ? 'true' : 'false'}>
                    {t('collab.requests.destinationSide')}:{' '}
                    {request.destination_approved
                      ? t('collab.requests.sideApproved')
                      : t('collab.requests.sidePending')}
                  </span>
                </span>
              )}
              {pending && canResolve && (
                <span className={styles.rowActions}>
                  {request.kind === 'board_transfer' && request.can_approve_destination && (
                    <NominateOwner
                      workspaceId={request.workspace_id}
                      value={nominees[request.id] ?? null}
                      onChange={(userId) => {
                        setNominees((current) => ({ ...current, [request.id]: userId }));
                      }}
                    />
                  )}
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={
                      resolve.isPending ||
                      (request.kind === 'board_transfer' &&
                        !request.can_approve_source &&
                        !request.can_approve_destination)
                    }
                    onClick={() => {
                      resolve.mutate({
                        requestId: request.id,
                        approve: true,
                        newOwnerUserId: nominees[request.id] ?? null,
                      });
                    }}
                  >
                    {t('collab.requests.approve')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={resolve.isPending}
                    onClick={() => {
                      resolve.mutate({ requestId: request.id, approve: false });
                    }}
                  >
                    {t('collab.requests.reject')}
                  </Button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** The receiving side may name who will own the transferred board (optional). */
function NominateOwner({
  workspaceId,
  value,
  onChange,
}: {
  workspaceId: string | null;
  value: string | null;
  onChange: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const members = useWorkspaceMembers(workspaceId ?? '', workspaceId !== null);
  return (
    <span className={styles.nominate}>
      <UserSelect
        label={t('collab.requests.nominateOwner')}
        users={members.data ?? []}
        value={value}
        placeholder={t('collab.requests.nominateOwner')}
        onChange={onChange}
      />
    </span>
  );
}
